import * as _ from 'underscore'
import { Enums } from '../common/enums'

import { DataProvider, ConvertHelper, TypeHelper } from '../../helpers'

import { ObjectCustomString } from '../../models/util/object-custom-string'

import { IReferenceType, IOpChangeState, IOpObject, ICopyable, IOpData } from '../util/interfaces'
import { OpDataList } from '../util/op-data-list'
import { Utils } from '../util/utils'

import { OpLocation } from './op-location'
import { OpTaskData } from './op-task-data'
import { OpTaskPaymentElement } from '../custom/op-task-payment-element'
import { OpTaskStatus } from './op-task-status'
import { OpTaskType } from './op-task-type'
import { OpTechnicalPermission } from './op-technical-permission'
import { OpDevice } from './op-device'

export class OpTask implements ICopyable<OpTask>, IOpChangeState, IOpObject, IReferenceType {

    //#region Properties

    public idTask: number = 0
    public idTaskType: number
    public idIssue: number
    public idTaskGroup: number
    public idPlannedRoute: number
    public idOperatorRegistering: number
    public idOperatorPreformer: number
    public idTaskStatus: number
    public idDistributor: number
    public creationDate: Date
    public accepted: boolean
    public idOperatorAccepted: number
    public acceptanceDate: Date
    public notes: string
    public deadline: Date
    public idLocation: number
    public idOperatorGroup: number

    //#endregion


    //#region Navigation Properties

    public location: OpLocation
    public taskStatus: OpTaskStatus
    public taskType: OpTaskType

    //#endregion


    //#region Custom Properties

    public payments: OpTaskPaymentElement[] = []
    public technicalPermissions: OpTechnicalPermission[] = []

    public installedDevices: OpDevice[] = []
    public uninstalledDevices: OpDevice[] = []

    public get additionalPayment(): boolean {
        return this.dataList.getValue(Enums.DataType.TASK_ADDITIONAL_PAYMENT)
    }

    public set additionalPayment(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.TASK_ADDITIONAL_PAYMENT)
    }

    public get clientOrderedToPay(): boolean {
        return this.dataList.getValue(Enums.DataType.TASK_CLIENT_ORDERED_TO_PAY)
    }

    public set clientOrderedToPay(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.TASK_CLIENT_ORDERED_TO_PAY)
    }

    public get clientPaymentAmount(): number {
        return this.dataList.getValue(Enums.DataType.TASK_CLIENT_PAYMENT_AMOUNT, 0, 0)
    }

    public set clientPaymentAmount(value: number) {
        this.dataList.setValue(value, Enums.DataType.TASK_CLIENT_PAYMENT_AMOUNT)
    }

    public get clientPaymentDate(): Date {
        const dateString: string = this.dataList.getValue(Enums.DataType.TASK_CLIENT_PAYMENT_DATE)

        if (dateString != null) {
            return ConvertHelper.stringToDate(dateString)
        }

        return null
    }

    public set clientPaymentDate(value: Date) {
        let dateString: string = null

        if (value != null) {
            dateString = ConvertHelper.dateToString(value)
        }

        this.dataList.setValue(dateString, Enums.DataType.TASK_CLIENT_PAYMENT_DATE)
    }

    public get clientPaymentDone(): boolean {
        return this.dataList.getValue(Enums.DataType.TASK_CLIENT_PAYMENT_DONE)
    }

    public set clientPaymentDone(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.TASK_CLIENT_PAYMENT_DONE)
    }

    public get fitterOrderedToPay(): boolean {
        return this.dataList.getValue(Enums.DataType.TASK_FITTER_ORDERED_TO_PAY)
    }

    public set fitterOrderedToPay(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.TASK_FITTER_ORDERED_TO_PAY)
    }

    public get fitterPaymentDate(): Date {
        const dateString: string = this.dataList.getValue(Enums.DataType.TASK_FITTER_PAYMENT_DATE)

        if (dateString != null) {
            return ConvertHelper.stringToDate(dateString)
        }

        return null
    }

    public set fitterPaymentDate(value: Date) {
        let dateString: string = null

        if (value != null) {
            dateString = ConvertHelper.dateToString(value)
        }

        this.dataList.setValue(dateString, Enums.DataType.TASK_FITTER_PAYMENT_DATE)
    }

    public get fitterPaymentDone(): boolean {
        return this.dataList.getValue(Enums.DataType.TASK_FITTER_PAYMENT_DONE)
    }

    public set fitterPaymentDone(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.TASK_FITTER_PAYMENT_DONE)
    }

    public get fitterVisitDate(): Date {
        const dateString: string = this.dataList.getValue(Enums.DataType.TASK_FITTER_VISIT_DATE)

        if (dateString != null) {
            return ConvertHelper.stringToDate(dateString)
        }

        return null
    }

    public set fitterVisitDate(value: Date) {
        let dateString: string = null

        if (value != null) {
            dateString = ConvertHelper.dateToString(value)
        }

        this.dataList.setValue(dateString, Enums.DataType.TASK_FITTER_VISIT_DATE)
    }

    public get paymentAmount(): number {
        return this.dataList.getValue(Enums.DataType.TASK_PAYMENT_AMOUNT, 0, 0)
    }

    public set paymentAmount(value: number) {
        this.dataList.setValue(value, Enums.DataType.TASK_PAYMENT_AMOUNT)
    }

    public get paymentNote(): string {
        return this.dataList.getValue(Enums.DataType.TASK_PAYMENT_NOTE)
    }

    public set paymentNote(value: string) {
        this.dataList.setValue(value, Enums.DataType.TASK_PAYMENT_NOTE)
    }

    //#endregion


    //#region Ctor

    constructor(opObj: any = null) {
        if (opObj instanceof OpTask) {
            this.copy(<OpTask>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#region


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdTask
    }

    public getReferenceKey(): number {
        return this.idTask
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idTask
    }

    public set idObject(idObject: any) {
        this.idTask = idObject
    }

    public dataList: OpDataList<OpTaskData> = new OpDataList<OpTaskData>(OpTaskData)

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idTask = opObj.ID_TASK
            this.idTaskStatus = opObj.ID_TASK_STATUS
            this.idIssue = opObj.ID_ISSUE
            this.idTaskGroup = opObj.ID_TASK_GROUP
            this.idPlannedRoute = opObj.ID_PLANNED_ROUTE
            this.idTaskType = opObj.ID_TASK_TYPE
            this.idLocation = opObj.ID_LOCATION
            this.idOperatorPreformer = opObj.ID_OPERATOR_PREFORMER
            this.idOperatorRegistering = opObj.ID_OPERATOR_REGISTERING
            this.idOperatorGroup = opObj.ID_OPERATOR_GROUP
            this.idOperatorAccepted = opObj.ID_OPERATOR_ACCEPTED
            this.notes = opObj.NOTES
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.opState = opObj.OpState

            if (opObj.DEADLINE != null) {
                this.deadline = ConvertHelper.stringToDate(opObj.DEADLINE)
            }

            if (opObj.CREATION_DATE != null) {
                this.creationDate = ConvertHelper.stringToDate(opObj.CREATION_DATE)
            }

            if (opObj.ACCEPTANCE_DATE != null) {
                this.acceptanceDate = ConvertHelper.stringToDate(opObj.ACCEPTANCE_DATE)
            }

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpTaskData(data)))
            }

            this.dynamicValues = opObj.dynamicValues

            this.taskStatus = opObj._TaskStatus ? new OpTaskStatus(opObj._TaskStatus) : null
            this.taskType = opObj._TaskType ? new OpTaskType(opObj._TaskType) : null
            this.location = opObj._Location ? new OpLocation(opObj._Location) : null

            this.installedDevices = opObj.InstalledDevices != null ? opObj.InstalledDevices.map((installedDeviceObj: any) => new OpDevice(installedDeviceObj)) : []
            this.uninstalledDevices = opObj.UninstalledDevices != null ? opObj.UninstalledDevices.map((installedDeviceObj: any) => new OpDevice(installedDeviceObj)) : []
            this.technicalPermissions = opObj.TechnicalPermissions != null ? opObj.TechnicalPermissions.map((technicalPermission: any) => new OpTechnicalPermission(technicalPermission)) : []

            this.payments = opObj.Payments != null ? opObj.Payments.map((paymentObj: any) => new OpTaskPaymentElement(paymentObj)) : []
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_TASK: this.idTask,
            ID_TASK_STATUS: this.idTaskStatus,
            ID_ISSUE: this.idIssue,
            ID_TASK_GROUP: this.idTaskGroup,
            ID_PLANNED_ROUTE: this.idPlannedRoute,
            ID_LOCATION: this.idLocation,
            ID_OPERATOR_ACCEPTED: this.idOperatorAccepted,
            ID_OPERATOR_GROUP: this.idOperatorGroup,
            ID_OPERATOR_PREFORMER: this.idOperatorPreformer,
            ID_OPERATOR_REGISTERING: this.idOperatorRegistering,
            CREATION_DATE: this.creationDate ? ConvertHelper.dateToString(this.creationDate, { netFormat: true, includeTimeZone: true }) : undefined,
            NOTES: this.notes,
            Payments: _.size(this.payments) > 0 ? this.payments.map((payment: OpTaskPaymentElement) => payment.toJSON()) : null,
            ID_DISTRIBUTOR: this.idDistributor,

            TechnicalPermissions: this.technicalPermissions != null ? this.technicalPermissions.map((technicalPermission: OpTechnicalPermission) => technicalPermission.toJSON(compressed)) : null,

            dynamicValues: this.dynamicValues,

            OpState: this.opState,
            DataList: this.dataList != null ? this.dataList.ToJSON() : null,
        }
    }

    //#endregion


    //#region ICopyable Implementation

    public copy(source: OpTask, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return
        }

        if (includeNavigationProperties) {
            if (source.location != null) {
                this.location = source.location.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
            if (source.taskStatus != null) {
                this.taskStatus = source.taskStatus
            }
            if (source.taskType != null) {
                this.taskType = source.taskType
            }
        }

        if (includeCustomProperties) {
            if (source.payments != null) {
                this.payments = TypeHelper.clone(source.payments)
            }

            if (source.technicalPermissions != null) {
                this.technicalPermissions = _.map(source.technicalPermissions, (technicalPermission: OpTechnicalPermission) => technicalPermission.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }

            if (source.installedDevices != null) {
                this.installedDevices = TypeHelper.clone(source.installedDevices)
            }

            if (source.uninstalledDevices != null) {
                this.uninstalledDevices = TypeHelper.clone(source.uninstalledDevices)
            }
        }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.idTask = source.idTask
        this.idTaskStatus = source.idTaskStatus
        this.idTaskType = source.idTaskType
        this.idLocation = source.idLocation
        this.creationDate = source.creationDate
        this.opState = source.opState
    }

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpTask {
        const newTask = new OpTask(null)

        newTask.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newTask
    }

    //#endregion


    //#region toString

    public toString(): string {
        const customFormatString = ObjectCustomString.getCustomStringFormat(this)

        if (customFormatString == null) {
            let taskType: string = TypeHelper.emptyString
            let location: string = TypeHelper.emptyString

            // task type
            if (this.taskType != null) {
                taskType = this.taskType.toString()
            } else if (this.idTaskType != null) {
                taskType = this.idTaskType.toString()
            }

            // location
            if (this.location != null) {
                location = this.location.toString()
            } else if (this.idLocation != null) {
                location = this.idLocation.toString()
            }

            return taskType + TypeHelper.semiColonSeparator + location
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpTask[]) {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    //#endregion

    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpTask {
        const newObject: OpTask = new OpTask(null)

        newObject.idTask = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpTaskData.fromIOpData(data)))

        return newObject
    }

    //#endregion
}